import {
    deleteFormEntry,
    deleteUpload,
    downloadFolderSelectionZip,
    fetchSelectionFormEntryIds,
    fetchSelectionStats,
    setFolderSelection,
    setFolderSelectionItems,
} from "@rpforms/shared/build/actions";
import PageNavAction from "@rpforms/shared/build/components/universal/PageNavAction";
import {
    fetchUploadDownloadUrl,
    openSignedUrl,
} from "@rpforms/shared/build/actions/signedDownloads";
import { push } from "connected-react-router";
import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import styled from "styled-components";
import { useGenerateOffer } from "../../hooks/useGenerateOffer";
import { RootState } from "../../reducers";
import EntityStatsModal from "../entities/EntityStatsModal";
import { FolderMerger } from "./FolderMerger";

import { uniqueArray } from "@rpforms/shared/build/utils";
import { FolderMover } from "./FolderMover";
import { MergePdfModal } from "./MergePdfModal";
import { FolderSearch } from "./FolderSearch";
import { UploadMover } from "./UploadMover";

const FolderToolbarWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 123px;
    > div {
        list-style: none;
        margin: 0;
        padding: 0;
    }
`;

export const FolderToolbar = ({ onSync, selectedFolder }) => {
    const dispatch = useDispatch();
    const { selection, selectionEnabled } = useSelector<RootState, any>((state) => state.directory);
    const [folderToMove, setFolderToMove] = useState(false);
    const [merger, setMergerVisible] = useState(false);
    const [uploadMover, setUploadMoverVisible] = useState(false);
    const [selectionStats, setSelectionStats] = useState(null);
    const [statsModalShow, setStatsModalShow] = useState(0);
    const [statsLoading, setStatsLoading] = useState(false);
    const [zipLoading, setZipLoading] = useState(false);
    const [mergePdfFolderIds, setMergePdfFolderIds] = useState(null);
    const [offerLoading, setOfferLoading] = useState(false);
    const {
        generate: generateOffer,
        isGenerating,
        error: offerError,
        notesModal,
    } = useGenerateOffer();

    const busy = statsLoading || zipLoading || offerLoading || isGenerating;
    const busyLabel = statsLoading
        ? "Lädt..."
        : zipLoading
        ? "ZIP wird erstellt..."
        : "Angebot wird erstellt...";

    useEffect(() => {
        if (offerError) {
            toast.error(offerError);
        }
    }, [offerError]);

    const toggleSelection = () => {
        dispatch(setFolderSelection(!selectionEnabled));
    };

    const resetSelection = () => {
        dispatch(setFolderSelection(false));
        dispatch(setFolderSelectionItems([]));
    };

    const moveSelection = () => {
        setFolderToMove(selection);
    };

    const moveUploadSelection = () => {
        setUploadMoverVisible(true);
    };

    const mergeSelection = () => {
        setMergerVisible(true);
    };

    const showSelectionStats = async () => {
        setStatsLoading(true);
        try {
            await dispatch(
                fetchSelectionStats(
                    selection.map((s) => s.id),
                    (stats) => {
                        setSelectionStats(stats);
                        setStatsModalShow(-1);
                    }
                )
            );
        } catch (e) {
            toast.error("Statistiken konnten nicht geladen werden");
        } finally {
            setStatsLoading(false);
        }
    };

    const downloadSelectionZip = async () => {
        setZipLoading(true);
        try {
            await dispatch(downloadFolderSelectionZip(selection.map((s) => s.id)));
        } catch (e) {
            toast.error(e.message);
        } finally {
            setZipLoading(false);
        }
    };

    const createOfferFromSelection = async () => {
        setOfferLoading(true);
        try {
            const entryIds = await dispatch(fetchSelectionFormEntryIds(selection.map((s) => s.id)));
            if (!entryIds || (entryIds as any).length === 0) {
                toast.error("In der Auswahl wurden keine Protokolle gefunden");
                return;
            }
            await generateOffer("offer", entryIds as any);
        } catch (e) {
            toast.error("Angebot konnte nicht erstellt werden");
        } finally {
            setOfferLoading(false);
        }
    };

    const open = () => {
        openSignedUrl(fetchUploadDownloadUrl(selectedFolder.resource.id));
    };

    const editFormEntry = () => {
        dispatch(push("/entries/entry-edit-" + selectedFolder.resource.id));
    };

    const removeUpload = () => {
        if (confirm("Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden.")) {
            dispatch(deleteUpload(selectedFolder.resource));
        }
    };

    const removeFormEntry = () => {
        if (confirm("Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden.")) {
            dispatch(deleteFormEntry(selectedFolder.resource));
        }
    };

    const openCustomer = () => {
        if (selectedFolder.resource_type === "Customer") {
            dispatch(push("/master_data/customers/view-" + selectedFolder.resource.id));
        } else {
            dispatch(push("/master_data/customers/view-" + selectedFolder.resource.customer_id));
        }
    };

    const sameSelectionResources = () => {
        return uniqueArray(selection.map((s2) => s2.resource_type)).length === 1;
    };

    // used for entities
    const onlyEntitiesSelected = () => {
        return (
            selectionEnabled &&
            selection.length > 0 &&
            selection.every((s) => s.movable && s.resource_type === "Entity") &&
            sameSelectionResources()
        );
    };

    // used for all folders below the entity level
    const onlyUploadsSelected = () => {
        return (
            selectionEnabled &&
            selection.length > 0 &&
            selection.every((s) => s.movable && s.resource_type !== "Entity") &&
            sameSelectionResources()
        );
    };

    const selectionMergable = () => {
        return (
            selectionEnabled &&
            selection.length > 1 &&
            selection.every((s) => s.mergable) &&
            sameSelectionResources()
        );
    };

    return (
        <FolderToolbarWrapper>
            <FolderSearch />
            {notesModal}
            <FolderMover folder={folderToMove} setVisibility={setFolderToMove} />
            <UploadMover folder={uploadMover} setVisibility={setUploadMoverVisible} />
            <FolderMerger folder={merger} setVisibility={setMergerVisible} />
            {mergePdfFolderIds && (
                <MergePdfModal
                    folderIds={mergePdfFolderIds}
                    onHide={() => setMergePdfFolderIds(null)}
                />
            )}
            {selectionStats && (
                <EntityStatsModal
                    entity={{
                        id: -1,
                        name: `Auswahl (${selection.length})`,
                        stats: selectionStats,
                    }}
                    settingsModalShow={statsModalShow}
                    setSettingsModalShow={setStatsModalShow}
                />
            )}
            <div>
                {(selectedFolder.resource_type === "Customer" ||
                    selectedFolder.resource_type === "Entity") && (
                    <PageNavAction title="Ansehen" icon="eye" onClick={openCustomer} />
                )}

                {selectedFolder.resource_type === "Upload" && (
                    <PageNavAction title="Ansehen" icon="eye" onClick={open} />
                )}
                {selectedFolder.resource_type === "FormEntry" && (
                    <PageNavAction title="Ansehen" icon="eye" onClick={editFormEntry} />
                )}

                {!selectionEnabled && (
                    <PageNavAction title="Markieren" icon="th-list" onClick={toggleSelection} />
                )}
                {selectionEnabled && selection.length === 0 && (
                    <PageNavAction title="Markieren..." icon="th-list" onClick={toggleSelection} />
                )}
                {onlyEntitiesSelected() && (
                    <PageNavAction title="Verschieben..." icon="th-list" onClick={moveSelection} />
                )}
                {onlyUploadsSelected() && (
                    <PageNavAction
                        title="Verschieben..."
                        icon="th-list"
                        onClick={moveUploadSelection}
                    />
                )}
                {selectionMergable() && (
                    <PageNavAction
                        title="Zusammenführen..."
                        icon="th-list"
                        onClick={mergeSelection}
                    />
                )}

                {selectionEnabled && selection.length > 0 && (
                    <Dropdown style={{ display: "inline-block" }}>
                        <Dropdown.Toggle
                            variant="light"
                            id="folder-selection-actions"
                            disabled={busy}
                        >
                            <i
                                className={`fa fa-fw fa-${
                                    busy ? "spinner fa-spin" : "check-square-o"
                                }`}
                                style={{ paddingRight: 4 }}
                            />
                            {busy ? busyLabel : `Auswahl (${selection.length})`}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={showSelectionStats}>
                                <i className="fa fa-fw fa-bar-chart" /> Statistiken
                            </Dropdown.Item>
                            <Dropdown.Item onClick={downloadSelectionZip}>
                                <i className="fa fa-fw fa-file-archive-o" /> ZIP herunterladen
                            </Dropdown.Item>
                            <Dropdown.Item onClick={createOfferFromSelection}>
                                <i className="fa fa-fw fa-file-text-o" /> Angebot erstellen
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => setMergePdfFolderIds(selection.map((s) => s.id))}
                            >
                                <i className="fa fa-fw fa-file-pdf-o" /> PDF zusammenführen...
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                {selectionEnabled && selection.length > 0 && (
                    <PageNavAction
                        danger={true}
                        title="Abbrechen"
                        icon="th-list"
                        onClick={resetSelection}
                    />
                )}

                <PageNavAction success={true} title="Sync" icon="repeat" onClick={onSync} />
            </div>
        </FolderToolbarWrapper>
    );
};
